import { Formik } from 'formik';
import React from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OrgManagerRole } from '@uplift/api';
import { EMPTY_STRING } from '@uplift/constants';
import {
  Alert, CancelButton, FormModal, ModalActions, ModalContent, Select, SubmitButton, TextInput
} from '@uplift/ui-v2';

import { adminSpecificManagerRoleOptions, managerRoleOptions } from '../helpers';
import { InviteManagerFormValues } from '../types';
import { inviteManagerFormValidation } from '../validation';

type InviteManagerModalProps = {
  onCancel: () => void;
  onSubmit: (values: InviteManagerFormValues) => void;
  accountExists: boolean;
  setAccountExists: (value: boolean) => void;
  alertMessage: string;
  isOrgAdmin?: boolean;
};

export const InviteManagerModal: React.VFC<InviteManagerModalProps> = ({ onCancel, onSubmit, isOrgAdmin, accountExists, alertMessage, setAccountExists }) => {
  const { classes, cx } = useInviteManagerModalStyles();

  const modalContent = isOrgAdmin ? 'Invite staff members who can create and manage student referrals.' : 'Invite university administrators to manage team member access and to make student referrals.';

  const initialValues: InviteManagerFormValues = {
    firstName: EMPTY_STRING,
    lastName: EMPTY_STRING,
    role: isOrgAdmin ? OrgManagerRole.staff : EMPTY_STRING as OrgManagerRole,
    email: EMPTY_STRING,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={inviteManagerFormValidation}
      validateOnMount
    >
      {formik => (
        <FormModal
          title="Invite Manager"
          onClose={onCancel}
          maxWidth="md"
          open
          data-testid="invite-manager-modal"
        >
          <Divider />
          <Box width="600px">
            <ModalContent>
              <Box display="flex" flexDirection="column" mb={3}>
                <Typography variant="body1">
                  {modalContent}
                </Typography>
                {isOrgAdmin && <Select name="role" label="Role" items={adminSpecificManagerRoleOptions} disabled className={cx(classes.input, classes.role)} />}
                <TextInput
                  name="firstName"
                  label="First Name"
                  className={classes.input}
                  data-testid="first-name-input"
                />
                <TextInput
                  name="lastName"
                  label="Last Name"
                  className={classes.input}
                  data-testid="last-name-input"
                />
                {!isOrgAdmin && <Select name="role" label="Role" items={managerRoleOptions} className={classes.input} />}
                <TextInput
                  name="email"
                  label="Email Address"
                  className={classes.input}
                  data-testid="email-input"
                  error={accountExists}
                  errorHelperText={accountExists ? 'Enter valid email address' : ''}
                  onInput={() => setAccountExists(false)}
                />
                {accountExists && <Alert severity="error" showIcon={false} className={classes.alert}>{alertMessage}</Alert>}
              </Box>
            </ModalContent>
            <Divider />
            <ModalActions>
              <CancelButton variant="text" color="secondary" onClick={onCancel} >
                Cancel
              </CancelButton>
              <SubmitButton
                variant="contained" color="secondary"
                loading={formik.isSubmitting}
                data-testid="send-invite-button"
              >
                Send Invite
              </SubmitButton>
            </ModalActions>
          </Box>
        </FormModal>
      )}
    </Formik>
  );
};

const useInviteManagerModalStyles = makeStyles({ name: 'InviteManagerModal' })(theme => ({
  input: {
    marginTop: theme.spacing(2),
    '& label.Mui-focused:not(.Mui-error)': {
      color: theme.palette.secondary.main,
    },
    '& div.Mui-focused:not(.Mui-error) fieldset': {
      borderColor: `${theme.palette.secondary.main} !important`,
    },
    '& input': {
      padding: theme.spacing(2, 1.5, 2, 1.5),
    },
  },
  role: {
    width: '340px',
  },
  alert: {
    marginTop: theme.spacing(2)
  },
}));
